import Button from '@alltrails/shared/denali/components/Button';
import CustomProvider from 'components/CustomProvider';
import PaymentFinePrint from 'components/PaymentFinePrint';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { hasErrors } from 'utils/UserFormHelper';

import * as baseStyles from '../../../styles/baseStyles.module.scss';
import CouponCode from '../CouponCode';
import CreditCardForm from '../CreditCardForm/CreditCardForm';
import FormButton from '../FormButton';
import PlanTypeCardCollection from '../pro/PlanTypeCardCollection';
import Wrapper from '../Wrapper';
import * as styles from './styles/UpdatePaymentForm.module.scss';

const error = <FormattedMessage defaultMessage="There was an error processing your payment. Please contact support." />;

class UpdatePaymentForm extends Component {
  constructor(props) {
    super(props);
    const { billing_first_name, billing_last_name, cc_expiration_month, cc_expiration_year } = props.formData;

    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        postalCode: ''
      },
      name: `${billing_first_name} ${billing_last_name}`,
      number: '',
      cvv: '',
      postalCode: '',
      serverError: null,
      submitting: false,
      activePlanType: props.initialPlanType,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false,
      couponError: '',
      isCouponInputExpanded: false,
      expiration: cc_expiration_month ? `${String(cc_expiration_month).padStart(2, '0')} / ${cc_expiration_year % 100}` : ''
    };
  }

  static ANNUAL_PLAN = 'annual';

  static PLANS = ['annual'];

  static GIFT_CARD = 'GiftCard';

  componentDidMount() {
    if (this.state.verifiedCoupon && this.props.reactivation) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, isCouponInputShown: true, submitting: false });
    }
  }

  handleTypeClick = activePlanType => {
    this.setState({ activePlanType }, this.handleRemoveCoupon);
  };

  handleCreditCardFormChange = event => {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, this.state.errors);
    this.setState({ ...payload });
  };

  handlePaymentEditSuccess = data => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe(data.redirectUrl, this.props.locale);
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  handleCouponCodeChange = event => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleShowCouponCodeInput = () => {
    this.setState({ isCouponInputExpanded: true });
  };

  handleApplyCoupon = () => {
    this.handleCouponVerification(this.state.couponCode);
  };

  handlePaymentFormError = err => {
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText).errors;
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = typeof errorObj.base[0] === 'string' ? errorObj.base[0] : error;
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }

      if (errorObj.cc_expiration_month) {
        errors.expiration = errorObj.cc_expiration_month[0];
      }

      if (errorObj.cc_expiration_year) {
        errors.expiration = errorObj.cc_expiration_year[0];
      }

      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
    } else {
      serverError = error;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  getSubmissionData() {
    const [cc_expiration_month, cc_expiration_year] = this.state.expiration.split(' / ');

    let data = {
      user: {
        cc_number: this.state.number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        authenticity_token: this.props.authenticityToken
      }
    };

    // requires additional params if currently in the reactivation flow
    if (this.props.reactivation) {
      data = {
        ...data,
        plan_type: this.state.activePlanType,
        user: {
          ...data.user,
          user_entered_coupon_code: this.state.verifiedCoupon ? this.state.verifiedCoupon.code : null,
          reactivating: true
        }
      };
    }

    return data;
  }

  noGiftCard() {
    if (!this.props.reactivation || !this.state.verifiedCoupon) {
      return true;
    }

    return this.state.verifiedCoupon.type !== UpdatePaymentForm.GIFT_CARD;
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };

    for (const name in this.state.errors) {
      validation = validatePaymentForm(name, this.state[name], this.state.errors);
      validationErrors = { ...validationErrors, ...validation.errors };
    }

    this.setState({ errors: validationErrors });

    return hasErrors(validationErrors);
  }

  submitCreditCardForm = e => {
    e.preventDefault();
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    if (this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    this.setState({ errors: {} });

    const submissionData = this.getSubmissionData();

    ServerCommunicationUtil.putApiEndpoint(
      this.props.updateBillingUrl,
      submissionData,
      this.handlePaymentEditSuccess,
      this.handlePaymentFormError,
      () => {},
      { 'X-Language-Locale': this.props.locale }
    );
  };

  handleCouponVerification = couponCode => {
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });
    const payload = {
      coupon_code: couponCode
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      payload,
      data => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
      },
      err => {
        this.setState({
          isCouponApplied: false,
          couponError: <FormattedMessage defaultMessage="This coupon code is invalid" />,
          submitting: false
        });
      },
      () => {}
    );
  };

  renderPlanOptions() {
    if (!this.props.reactivation) {
      return;
    }

    return (
      <>
        <PlanTypeCardCollection
          activePlanType={this.state.activePlanType}
          currencyCode={this.props.currencyCode}
          handler={this.handleTypeClick}
          isCouponApplied={this.state.isCouponApplied}
          isSuccess={this.state.isCouponApplied}
          planTypes={this.props.planTypes}
          verifiedCoupon={this.state.verifiedCoupon}
        />
        <div className={styles.divider} />
      </>
    );
  }

  renderCreditCardForm() {
    return (
      <>
        {this.props.reactivation && (
          <div className={styles.billingText}>
            <FormattedMessage defaultMessage="Enter billing information" />
          </div>
        )}
        <form className={styles.form} id="update-payment-form" onSubmit={this.submitCreditCardForm}>
          <CreditCardForm
            changeHandler={this.handleCreditCardFormChange}
            cvv={this.state.cvv}
            errors={this.state.errors}
            expiration={this.state.expiration}
            isCouponInputExpanded={this.state.isCouponInputExpanded}
            isCouponInputShown={this.state.isCouponInputShown}
            name={this.state.name}
            number={this.state.number}
            postalCode={this.state.postalCode}
            setError={(name, error) => this.setState({ errors: { ...this.state.errors, [name]: error } })}
            submitHandler={this.submitCreditCardForm}
            toggleCouponCode={() => this.setState({ isCouponInputExpanded: !this.state.isCouponInputExpanded })}
          />
          {this.props.reactivation && (
            <CouponCode
              applyHandler={this.handleApplyCoupon}
              changeHandler={this.handleCouponCodeChange}
              errorMessage={this.state.couponError}
              expanded={this.state.isCouponInputExpanded}
              isSuccess={this.state.isCouponApplied}
              removeHandler={this.handleRemoveCoupon}
              value={this.state.couponCode}
            />
          )}
          {this.state.serverError && <div className={styles.serverError}>{this.state.serverError}</div>}
          <FormButton disabled={this.state.submitting} value={<FormattedMessage defaultMessage="Submit" />} />
          {!this.props.reactivation && (
            <Button linkInfo={{ href: '/my/profile/edit' }} text={<FormattedMessage defaultMessage="Cancel" />} variant="flat" />
          )}
        </form>
      </>
    );
  }

  render() {
    return (
      <CustomProvider>
        <Wrapper
          extraContainerClass={styles.wrapperContainerClasses}
          foot={
            this.props.reactivation &&
            this.state.activePlanType === UpdatePaymentForm.ANNUAL_PLAN && (
              <PaymentFinePrint
                activePlanType={this.state.activePlanType}
                coupon={this.state.verifiedCoupon}
                currencyCode={this.props.currencyCode}
                isCouponApplied={this.props.isCouponApplied}
                plan={this.props.planTypes.find(planType => planType.planCode === this.state.activePlanType)}
              />
            )
          }
          text={
            this.props.reactivation
              ? { headText: <FormattedMessage defaultMessage="Pick your plan" /> }
              : { headText: <FormattedMessage defaultMessage="Credit Card" /> }
          }
        >
          {this.renderPlanOptions()}
          <div className={baseStyles.section}>
            <div>{this.renderCreditCardForm()}</div>
          </div>
        </Wrapper>
      </CustomProvider>
    );
  }
}

UpdatePaymentForm.defaultProps = {};

UpdatePaymentForm.propTypes = {
  locale: PropTypes.string,
  recurlyPublicKey: PropTypes.string,
  updateBillingUrl: PropTypes.string,
  initialPlanType: PropTypes.string,
  formAuthenticityToken: PropTypes.string,
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      planCode: PropTypes.string,
      priceMonth: PropTypes.string,
      priceTotal: PropTypes.string,
      numberOfMonths: PropTypes.string,
      showDiscount: PropTypes.bool,
      allowCoupon: PropTypes.bool
    })
  ),
  formData: PropTypes.shape({
    billing_first_name: PropTypes.string,
    billing_last_name: PropTypes.string,
    cc_expiration_month: PropTypes.number,
    cc_expiration_year: PropTypes.number
  })
};

export default UpdatePaymentForm;
